import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { photographerService } from '../../services/photographerService';
import { supabase } from '../../lib/supabase';
import AppIcon from '../../components/AppIcon';
import DroneIcon from '../../components/icons/DroneIcon';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import { CheckboxGroup, Checkbox } from '../../components/ui/Checkbox';
import Toast from '../../components/ui/Toast';
import { cn } from '../../utils/cn';
import LocationPickerMap from '../../components/LocationPickerMap';
import { extractCityDistrict } from '../../utils/addressParsing';
import { calculateDistance } from '../../services/locationService';
import IdentityVerificationSection from './components/IdentityVerificationSection';
import { deleteOwnAccountViaEdgeFunction } from '../../services/userDeleteAccountClient';
import { Capacitor } from '@capacitor/core';
import { getCurrentPosition, isGeolocationSupported } from '../../utils/location';
import { registerMobilePush, unregisterMobilePush } from '../../services/mobilePushService';

const NOTIF_KEY = 'mashhad_notifications_enabled';

const Profile = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname || '';
  const isHub = pathname === '/photographer-dashboard/profile' || pathname.endsWith('/profile');
  const isForm = pathname.includes('/profile/form');
  const isServiceArea = pathname.includes('/profile/service-area');
  const isCertificates = pathname.includes('/profile/certificates');
  const isVerification = pathname.includes('/profile/verification');
  const isSettings = pathname.includes('/profile/settings');
  const { userProfile, signOut } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [photographerData, setPhotographerData] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [approvedPortfolioCount, setApprovedPortfolioCount] = useState(0);
  const [activePortfolioCount,   setActivePortfolioCount]   = useState(0); // معتمدة + قيد المراجعة
  const [rejectedPortfolioCount, setRejectedPortfolioCount] = useState(0);
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    city: '',
    bio: '',
    specializations: [], // Changed to array
    equipment: [], // Changed to array
    shootingMethods: [],      // Shooting methods: professional_camera, mobile_camera, drone
    shootingMethodModels: {}, // Device model per method {"professional_camera":"Canon EOS R5",...}
    responseTime: 2,
    specialSkills: [],
    contactEmail: '',
    entityType: 'individual', // نوع الحساب: individual = فرد، entity = استوديو (من التسجيل، للعرض فقط)
    languages: [],
    hasStudio: false,
    studioLocation: null, // { address_text, city, district, street, postal_code, lat, lng }
    // Service area fields
    serviceCenterLat: null,
    serviceCenterLng: null,
    serviceRadiusKm: 20,
    serviceAreaLabel: '',
  });
  const [serviceMapVisible, setServiceMapVisible] = useState(false);
  const [savingServiceArea, setSavingServiceArea] = useState(false);
  const [currentAvatarUrl, setCurrentAvatarUrl] = useState(null); // For immediate preview
  // SAFE MODE: Certificates feature - list from profile, managed locally then saved
  const [certificates, setCertificates] = useState([]);
  const [uploadingCertificates, setUploadingCertificates] = useState(false);
  const [deletingCertIndex, setDeletingCertIndex] = useState(null);
  const certificateInputRef = React.useRef(null);
  // Latest service center coordinates (avoids stale formData when saving quickly after selection)
  const serviceCenterRef = React.useRef({ lat: null, lng: null });
  const CERTIFICATE_ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];
  const CERTIFICATE_MAX_SIZE = 5 * 1024 * 1024; // 5MB
  const [deletingAccount, setDeletingAccount] = useState(false);

  const isNative = Capacitor.isNativePlatform();
  const [notificationsEnabled, setNotificationsEnabled] = useState(() => {
    const stored = localStorage.getItem(NOTIF_KEY);
    return stored === null ? true : stored === 'true';
  });
  const handleToggleNotifications = async (enabled) => {
    setNotificationsEnabled(enabled);
    localStorage.setItem(NOTIF_KEY, String(enabled));
    if (isNative) {
      if (enabled) {
        await registerMobilePush(user?.id).catch(() => {});
      } else {
        await unregisterMobilePush().catch(() => {});
      }
    }
  };

  const cities = [
    'الرياض',
    'جدة',
    'الدمام',
    'الخبر',
    'المدينة المنورة',
    'مكة المكرمة',
    'الطائف',
    'أبها',
    'خميس مشيط',
    'جازان',
    'نجران',
    'الباحة',
    'تبوك',
    'حائل',
    'بريدة',
    'سكاكا',
  ];

  // Predefined Photography Categories — 6 types matching the booking flow
  const photographyCategories = [
    { value: 'event', label: 'تصوير المناسبات' },
    { value: 'product', label: 'تصوير المنتجات' },
    { value: 'portrait', label: 'التصوير الشخصي' },
    { value: 'food', label: 'تصوير الطعام' },
    { value: 'real-estate', label: 'تصوير العقارات' },
    { value: 'wedding', label: 'حفلات الزفاف' },
  ];

  // Predefined Equipment List
  const equipmentOptions = [
    { value: 'basic-editing',        label: 'تعديل أساسي (تصحيح إضاءة وألوان)' },
    { value: 'professional-editing', label: 'تعديل احترافي متقدم' },
  ];

  // Shooting Methods Options
  const shootingMethodOptions = [
    { value: 'professional_camera', label: 'كاميرا احترافية', icon: 'Camera',    placeholder: 'مثال: Canon EOS R5, Sony A7 IV' },
    { value: 'mobile_camera',       label: 'كاميرا جوال',     icon: 'Smartphone', placeholder: 'مثال: iPhone 15 Pro, Samsung S24' },
    { value: 'drone',               label: 'تصوير جوي (درون)', icon: 'Radio',    placeholder: 'مثال: DJI Mini 4 Pro, Autel Evo' },
  ];

  useEffect(() => {
    if (userProfile?.id && !hasLoaded) {
      loadProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile?.id]);


  const loadProfile = async () => {
    try {
      setLoading(true);

      // نعتمد على userProfile من AuthContext فقط — لا نستدعي getUser() مباشرةً
      // لمنع التنافس على قفل Supabase Auth عند أول تحميل بعد التسجيل
      const userId = userProfile?.id;

      if (!userId) {
        console.warn('No user ID available — waiting for AuthContext');
        setLoading(false);
        return;
      }
      
      const photographer = await photographerService.getPhotographer(userId);
      setPhotographerData(photographer);

      // تحميل إحصاءات صور المعرض
      const { count: approvedCount } = await supabase
        .from('portfolio_images')
        .select('*', { count: 'exact', head: true })
        .eq('photographer_id', userId)
        .eq('approval_status', 'approved');
      setApprovedPortfolioCount(approvedCount || 0);

      const { count: activeCount } = await supabase
        .from('portfolio_images')
        .select('*', { count: 'exact', head: true })
        .eq('photographer_id', userId)
        .in('approval_status', ['approved', 'pending']);
      setActivePortfolioCount(activeCount || 0);

      const { count: rejectedCount } = await supabase
        .from('portfolio_images')
        .select('*', { count: 'exact', head: true })
        .eq('photographer_id', userId)
        .eq('approval_status', 'rejected');
      setRejectedPortfolioCount(rejectedCount || 0);

      // Reset currentAvatarUrl when loading profile (use DB avatar from photographer or user)
      if (photographer?.avatar_url || photographer?.avatarUrl || photographer?.user?.avatar_url) {
        setCurrentAvatarUrl(null); // Prefer photographer_profiles.avatar_url or user_profiles.avatar_url
      }

      if (photographer) {
        // Convert specializations to array of values
        let specializationsArray = [];
        if (Array.isArray(photographer.specializations)) {
          // Map existing values to our predefined categories
          specializationsArray = photographer.specializations
            .map((spec) => {
              // Try to find matching category
              const match = photographyCategories.find(
                (cat) => cat.label === spec || cat.value === spec
              );
              return match ? match.value : null;
            })
            .filter(Boolean);
        }

        // Convert equipment to array of values
        let equipmentArray = [];
        const equipmentData = photographer.equipmentAvailable || photographer.equipment_available;
        if (equipmentData) {
          if (Array.isArray(equipmentData)) {
            equipmentArray = equipmentData
              .map((eq) => {
                const match = equipmentOptions.find(
                  (opt) => opt.label === eq || opt.value === eq
                );
                return match ? match.value : null;
              })
              .filter(Boolean);
          }
        }

        // Get special skills and languages
        const specialSkillsData = photographer.specialSkills || photographer.special_skills || [];
        const languagesData = photographer.languages || [];
        
        // Get shooting methods
        const shootingMethodsData = photographer.shootingMethods || photographer.shooting_methods || [];
        const shootingMethodsArray = Array.isArray(shootingMethodsData) ? shootingMethodsData : [];
        const shootingMethodModelsData = photographer.shootingMethodModels || photographer.shooting_method_models || {};

        setFormData({
          fullName: photographer.user?.fullName || userProfile?.fullName || '',
          phone: photographer.user?.phone || userProfile?.phone || '',
          city: photographer.city || '',
          bio: photographer.bio || '',
          businessName: photographer.businessName || photographer.business_name || '',
          experienceYears: photographer.experienceYears || photographer.experience_years || 0,
          portfolioUrl: photographer.portfolioUrl || photographer.portfolio_url || '',
          specializations: specializationsArray,
          equipment: equipmentArray,
          shootingMethods: shootingMethodsArray,
          shootingMethodModels: typeof shootingMethodModelsData === 'object' && shootingMethodModelsData !== null ? shootingMethodModelsData : {},
          responseTime: photographer.responseTime || photographer.response_time || 2,
          specialSkills: Array.isArray(specialSkillsData) ? specialSkillsData : [],
          contactEmail: photographer.contact_email || photographer.contactEmail || photographer.user?.email || userProfile?.email || '',
          entityType: photographer.entity_type || photographer.entityType || 'individual',
          languages: Array.isArray(languagesData) ? languagesData : [],
          hasStudio: photographer.hasStudio || photographer.has_studio || false,
          studioLocation: photographer.studioLocation || photographer.studio_location || null,
          // Service area fields
          serviceCenterLat: photographer.serviceCenterLat || photographer.service_center_lat || null,
          serviceCenterLng: photographer.serviceCenterLng || photographer.service_center_lng || null,
          serviceRadiusKm: photographer.serviceRadiusKm || photographer.service_radius_km || 20,
          serviceAreaLabel: photographer.serviceAreaLabel || photographer.service_area_label || '',
        });
        const slat = photographer.serviceCenterLat || photographer.service_center_lat || null;
        const slng = photographer.serviceCenterLng || photographer.service_center_lng || null;
        if (slat != null && slng != null) {
          serviceCenterRef.current = { lat: slat, lng: slng };
        }
      }
      // SAFE MODE: Certificates feature - load certificates from profile
      const certs = photographer?.certificates ?? [];
      setCertificates(Array.isArray(certs) ? certs : []);
      setHasLoaded(true);
    } catch (error) {
      console.error('Failed to load profile:', error);
      setToast({ type: 'error', message: 'فشل تحميل الملف الشخصي' });
    } finally {
      setLoading(false);
    }
  };

  // City coordinates for map center fallback (Arabic keys from profile dropdown)
  const cityCoordinates = {
    'الرياض': { lat: 24.7136, lng: 46.6753 },
    'جدة': { lat: 21.4858, lng: 39.1925 },
    'الدمام': { lat: 26.4207, lng: 50.0888 },
    'الخبر': { lat: 26.2172, lng: 50.1971 },
    'المدينة المنورة': { lat: 24.5247, lng: 39.5692 },
    'مكة المكرمة': { lat: 21.3891, lng: 39.8579 },
    'الطائف': { lat: 21.2703, lng: 40.4158 },
    'أبها': { lat: 18.2164, lng: 42.5042 },
    'خميس مشيط': { lat: 18.3000, lng: 42.7333 },
    'جازان': { lat: 16.8894, lng: 42.5706 },
    'نجران': { lat: 17.4924, lng: 44.1277 },
    'الباحة': { lat: 20.0129, lng: 41.4677 },
    'تبوك': { lat: 28.3998, lng: 36.5700 },
    'حائل': { lat: 27.5114, lng: 41.7208 },
    'بريدة': { lat: 26.3260, lng: 43.9750 },
    'سكاكا': { lat: 29.9697, lng: 40.2064 },
  };
  // photographer_profiles.city may store English keys (riyadh, jeddah, dammam) per migration 20260229000000
  const cityCoordinatesEnglish = {
    riyadh: cityCoordinates['الرياض'],
    jeddah: cityCoordinates['جدة'],
    dammam: cityCoordinates['الدمام'],
    mecca: cityCoordinates['مكة المكرمة'],
    medina: cityCoordinates['المدينة المنورة'],
    taif: cityCoordinates['الطائف'],
    abha: cityCoordinates['أبها'],
    tabuk: cityCoordinates['تبوك'],
    najran: cityCoordinates['نجران'],
    jazan: cityCoordinates['جازان'],
    hail: cityCoordinates['حائل'],
    buraidah: cityCoordinates['بريدة'],
    khobar: cityCoordinates['الخبر'],
  };

  const getCityCenter = (cityName) => {
    const key = typeof cityName === 'string' ? cityName.trim() : '';
    return cityCoordinates[key] || cityCoordinatesEnglish[key?.toLowerCase()] || cityCoordinates['الرياض'];
  };

  // Reverse geocode for service area label
  const reverseGeocodeServiceArea = async (lat, lng) => {
    const googleMapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    if (!googleMapsApiKey) {
      return formData.city || 'الرياض';
    }

    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&language=ar&key=${googleMapsApiKey}`
      );
      const data = await response.json();
      
      if (data.status === 'OK' && data.results && data.results.length > 0) {
        const geocoderResult = data.results[0];
        const { city, district } = extractCityDistrict(geocoderResult);
        return district ? `${city || formData.city || 'الرياض'} — ${district}` : (city || formData.city || 'الرياض');
      }
    } catch (error) {
      console.error('Geocoding error:', error);
    }
    return formData.city || 'الرياض';
  };

  // Get city name only from coordinates (for service center city vs base city comparison)
  const getCityFromLatLng = async (lat, lng) => {
    const googleMapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    if (!googleMapsApiKey) return null;
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&language=ar&key=${googleMapsApiKey}`
      );
      const data = await response.json();
      if (data.status === 'OK' && data.results && data.results.length > 0) {
        const { city } = extractCityDistrict(data.results[0]);
        return city || null;
      }
    } catch (error) {
      console.error('Geocoding error:', error);
    }
    return null;
  };

  // دالة مشتركة: تحفظ نطاق الخدمة مباشرة في قاعدة البيانات
  const _persistServiceArea = async (lat, lng, radiusKm, label) => {
    try {
      const user = { id: userProfile?.id };
      if (!user.id) return;
      const { error } = await supabase
        .from('photographer_profiles')
        .update({
          service_center_lat: Number(lat),
          service_center_lng: Number(lng),
          service_radius_km: radiusKm || 20,
          service_area_label: label || null,
          updated_at: new Date().toISOString(),
        })
        .eq('user_id', user.id);
      if (error) throw error;
    } catch (err) {
      console.error('Failed to persist service area:', err);
      throw err;
    }
  };

  // Handle use current location for service area
  const handleUseCurrentLocationForService = async () => {
    if (!isGeolocationSupported()) {
      setToast({ type: 'error', message: 'المتصفح لا يدعم تحديد الموقع الجغرافي' });
      return;
    }

    const hostname = window.location.hostname;
    const isLocalNetwork = /^(192\.168\.|10\.|172\.(1[6-9]|2[0-9]|3[0-1])\.)/.test(hostname);
    const isSecure = window.isSecureContext || 
                     window.location.protocol === 'https:' || 
                     hostname === 'localhost' || 
                     hostname === '127.0.0.1' ||
                     isLocalNetwork;

    if (!isSecure) {
      setToast({ type: 'error', message: 'لتفعيل تحديد الموقع، افتح الموقع عبر https أو عبر localhost.' });
      return;
    }

    try {
      setSavingServiceArea(true);
      const position = await getCurrentPosition({
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      });

      const { latitude, longitude } = position.coords;

      const validation = await validateServiceCenterDistance(latitude, longitude);
      if (!validation.allowed) {
        setToast({ type: 'error', message: validation.message });
        setSavingServiceArea(false);
        return;
      }

      const serviceAreaLabel = await reverseGeocodeServiceArea(latitude, longitude);
      const currentRadius = formData.serviceRadiusKm || 20;

      // حفظ تلقائي فور التحديد
      await _persistServiceArea(latitude, longitude, currentRadius, serviceAreaLabel);

      serviceCenterRef.current = { lat: latitude, lng: longitude };
      setFormData((prev) => ({
        ...prev,
        serviceCenterLat: latitude,
        serviceCenterLng: longitude,
        serviceAreaLabel,
      }));

      setToast({ type: 'success', message: '✅ تم تحديد موقعك وحفظ نطاق الخدمة تلقائياً' });
    } catch (error) {
      console.error('Geolocation error:', error);
      setToast({ type: 'error', message: 'فشل تحديد موقعك. يرجى تحديد الموقع على الخريطة يدوياً.' });
    } finally {
      setSavingServiceArea(false);
    }
  };

  // Validate service center is within 100km of photographer's city (shared by map select and save)
  const validateServiceCenterDistance = async (lat, lng) => {
    const user = { id: userProfile?.id };
    if (!user.id) return { allowed: false, message: 'يجب تسجيل الدخول' };
    const { data: profileRow, error: profileError } = await supabase
      .from('photographer_profiles')
      .select('city')
      .eq('user_id', user.id)
      .maybeSingle();
    if (profileError) return { allowed: false, message: 'فشل تحميل الملف الشخصي' };
    if (!profileRow || profileRow.city == null || String(profileRow.city).trim() === '') {
      return { allowed: false, message: 'يرجى تحديد المدينة في الملف الشخصي (حسابي → الملف الشخصي) وحفظها أولاً.' };
    }
    const photographerCity = String(profileRow.city).trim();
    const cityCenter = getCityCenter(photographerCity);
    const distanceKm = calculateDistance(
      Number(cityCenter.lat),
      Number(cityCenter.lng),
      Number(lat),
      Number(lng)
    );
    if (Number.isNaN(distanceKm) || distanceKm < 0 || distanceKm > 100) {
      return { allowed: false, message: 'لا يمكن تعيين مركز نطاق الخدمة خارج مدينتك الأساسية (الحد الأقصى ١٠٠ كم).' };
    }
    return { allowed: true };
  };

  // Handle service location selection from map
  const handleServiceLocationSelect = async (locationData) => {
    const { lat, lng, city, district } = locationData;

    const validation = await validateServiceCenterDistance(lat, lng);
    if (!validation.allowed) {
      setToast({ type: 'error', message: validation.message });
      return;
    }

    // Build label from city and district if available
    let finalLabel = '';
    if (city && district) {
      finalLabel = `${city} — ${district}`;
    } else if (city) {
      finalLabel = city;
    } else {
      finalLabel = await reverseGeocodeServiceArea(lat, lng);
    }

    const currentRadius = formData.serviceRadiusKm || 20;

    try {
      setSavingServiceArea(true);
      // حفظ تلقائي فور اختيار الموقع من الخريطة
      await _persistServiceArea(lat, lng, currentRadius, finalLabel);

      serviceCenterRef.current = { lat, lng };
      setFormData((prev) => ({
        ...prev,
        serviceCenterLat: lat,
        serviceCenterLng: lng,
        serviceAreaLabel: finalLabel,
      }));
      setServiceMapVisible(false);
      setToast({ type: 'success', message: '✅ تم تحديد الموقع وحفظ نطاق الخدمة تلقائياً' });
    } catch {
      setToast({ type: 'error', message: 'فشل حفظ نطاق الخدمة. يرجى المحاولة مجدداً.' });
    } finally {
      setSavingServiceArea(false);
    }
  };

  // Save service area
  const handleSaveServiceArea = async () => {
    // Prefer ref (latest selection) to avoid stale formData when user clicks save quickly
    const refLat = serviceCenterRef.current?.lat;
    const refLng = serviceCenterRef.current?.lng;
    const hasRef = refLat != null && refLng != null;
    const hasForm = formData.serviceCenterLat != null && formData.serviceCenterLng != null;
    if (!hasRef && !hasForm) {
      setToast({ type: 'error', message: 'يرجى تحديد مركز منطقة الخدمة على الخريطة' });
      return;
    }
    const serviceLat = hasRef ? Number(refLat) : Number(formData.serviceCenterLat);
    const serviceLng = hasRef ? Number(refLng) : Number(formData.serviceCenterLng);

    const userId = userProfile?.id;
    if (!userId) {
      setToast({ type: 'error', message: 'يجب تسجيل الدخول' });
      return;
    }

    // المدينة محفوظة في photographer_profiles.city — نقرأها من القاعدة للتحقق من المسافة
    const { data: profileRow, error: profileError } = await supabase
      .from('photographer_profiles')
      .select('city')
      .eq('user_id', userId)
      .maybeSingle();

    if (profileError) {
      setToast({ type: 'error', message: 'فشل تحميل الملف الشخصي' });
      return;
    }
    if (!profileRow || profileRow.city == null || String(profileRow.city).trim() === '') {
      setToast({ type: 'error', message: 'يرجى تحديد المدينة في الملف الشخصي (حسابي → الملف الشخصي) وحفظها أولاً.' });
      return;
    }

    const photographerCity = String(profileRow.city).trim();
    const cityCenter = getCityCenter(photographerCity);
    if (Number.isNaN(serviceLat) || Number.isNaN(serviceLng)) {
      setToast({ type: 'error', message: 'موقع نطاق الخدمة غير صالح.' });
      return;
    }

    const distanceKm = calculateDistance(
      Number(cityCenter.lat),
      Number(cityCenter.lng),
      serviceLat,
      serviceLng
    );
    if (Number.isNaN(distanceKm) || distanceKm < 0 || distanceKm > 100) {
      setToast({ type: 'error', message: 'لا يمكن تعيين مركز نطاق الخدمة خارج مدينتك الأساسية (الحد الأقصى ١٠٠ كم).' });
      return;
    }

    try {
      setSavingServiceArea(true);

      const { error } = await supabase
        .from('photographer_profiles')
        .update({
          service_center_lat: serviceLat,
          service_center_lng: serviceLng,
          service_radius_km: formData.serviceRadiusKm || 20,
          service_area_label: formData.serviceAreaLabel || null,
          updated_at: new Date().toISOString(),
        })
        .eq('user_id', userId);

      if (error) throw error;

      // حدّث الـ state مباشرة بدلاً من إعادة تحميل كامل حتى لا يختفي المحتوى
      setFormData((prev) => ({
        ...prev,
        serviceCenterLat: serviceLat,
        serviceCenterLng: serviceLng,
        serviceRadiusKm: formData.serviceRadiusKm || 20,
        serviceAreaLabel: formData.serviceAreaLabel || prev.serviceAreaLabel || '',
      }));
      serviceCenterRef.current = { lat: serviceLat, lng: serviceLng };
      setToast({ type: 'success', message: 'تم حفظ نطاق الخدمة بنجاح' });
    } catch (error) {
      console.error('Failed to save service area:', error);
      setToast({ type: 'error', message: 'فشل حفظ نطاق الخدمة' });
    } finally {
      setSavingServiceArea(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Validate shooting methods
      if (!formData.shootingMethods || formData.shootingMethods.length === 0) {
        setToast({ type: 'error', message: 'يجب اختيار وسيلة تصوير واحدة على الأقل' });
        setSaving(false);
        return;
      }
      // Validate that every selected shooting method has a model/device name
      const missingModels = formData.shootingMethods.filter(
        (m) => !(formData.shootingMethodModels?.[m] || '').trim()
      );
      if (missingModels.length > 0) {
        setToast({ type: 'error', message: 'يرجى إدخال نوع الجهاز لكل وسيلة تصوير محددة' });
        setSaving(false);
        return;
      }
      
      const userId = userProfile?.id;
      if (!userId) throw new Error('Not authenticated');
      
      console.log('Saving shooting methods:', formData.shootingMethods);

      // Update user profile
      const { error: userError } = await supabase
        .from('user_profiles')
        .update({
          full_name: formData.fullName,
          email: (formData.contactEmail || '').trim() || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', userId);

      if (userError) throw userError;

      // Convert specializations array to labels for storage
      const specializationsLabels = formData.specializations.map((val) => {
        const category = photographyCategories.find((cat) => cat.value === val);
        return category ? category.label : val;
      });

      // Convert equipment array to labels for storage
      const equipmentLabels = formData.equipment.map((val) => {
        const equipment = equipmentOptions.find((opt) => opt.value === val);
        return equipment ? equipment.label : val;
      });

      // Ensure shooting_methods is an array
      const shootingMethodsArray = Array.isArray(formData.shootingMethods) 
        ? formData.shootingMethods 
        : formData.shootingMethods 
          ? [formData.shootingMethods] 
          : [];

      // Build update data, only include fields that have values
      const updateData = {
        city: formData.city || null,
        bio: formData.bio || null,
        business_name: formData.businessName || null,
        experience_years: formData.experienceYears || 0,
        portfolio_url: formData.portfolioUrl || null,
        specializations: specializationsLabels.length > 0 ? specializationsLabels : null,
        equipment_available: equipmentLabels.length > 0 ? equipmentLabels : null,
        shooting_methods: shootingMethodsArray.length > 0 ? shootingMethodsArray : [],
        shooting_method_models: formData.shootingMethodModels || {},
        response_time: formData.responseTime || 2,
        special_skills: Array.isArray(formData.specialSkills) && formData.specialSkills.length > 0 ? formData.specialSkills : null,
        contact_email: (formData.contactEmail || '').trim() || null,
        languages: Array.isArray(formData.languages) && formData.languages.length > 0 ? formData.languages : null,
        has_studio: formData.hasStudio || false,
        updated_at: new Date().toISOString(),
      };

      // Only include studio_location if it's a valid object
      if (formData.studioLocation && typeof formData.studioLocation === 'object') {
        updateData.studio_location = formData.studioLocation;
      } else {
        updateData.studio_location = null;
      }

      const { error: photographerError, data: updateResult } = await supabase
        .from('photographer_profiles')
        .update(updateData)
        .eq('user_id', userId)
        .select('shooting_methods');

      if (photographerError) {
        console.error('Photographer update error:', photographerError);
        throw photographerError;
      }

      console.log('Update result:', updateResult);

      // profile_completion_pct يُحدَّث تلقائياً عبر DB trigger عند أي تعديل

      setToast({ type: 'success', message: 'تم حفظ الملف الشخصي بنجاح' });
      loadProfile();
    } catch (error) {
      console.error('Failed to save profile:', error);
      setToast({ type: 'error', message: 'فشل حفظ الملف الشخصي' });
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      setToast({ type: 'error', message: 'لم يتم اختيار ملف' });
      return;
    }

    if (!userProfile?.id) {
      setToast({ type: 'error', message: 'المستخدم غير مسجل دخول' });
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setToast({ type: 'error', message: 'الملف المحدد ليس صورة' });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setToast({ type: 'error', message: 'حجم الصورة كبير جداً (الحد الأقصى 5MB)' });
      return;
    }

    try {
      setSaving(true);
      setToast(null);
      const user = { id: userProfile?.id };
      if (!user.id) {
        throw new Error('المستخدم غير مسجل دخول');
      }

      console.log('📸 Starting avatar upload:', {
        userId: user.id,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type
      });

      const rawExt = (file.name.split('.').pop() || '').toLowerCase().replace(/[^a-z0-9]/g, '');
      const fileExt = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(rawExt) ? rawExt : 'jpg';
      const timestamp = Date.now();
      const randomStr = Math.random().toString(36).substring(2, 9);
      const fileName = `${user.id}-${timestamp}-${randomStr}.${fileExt}`;
      const filePath = fileName;

      // Delete old avatar if exists (strip query params from URL for correct path)
      const oldAvatarUrl = photographerData?.avatar_url || photographerData?.avatarUrl || userProfile?.avatar_url;
      if (oldAvatarUrl) {
        try {
          const oldUrl = oldAvatarUrl;
          const pathPart = oldUrl.split('?')[0];
          const urlParts = pathPart.split('/');
          const lastPart = urlParts[urlParts.length - 1];
          const maybeFolder = urlParts.length >= 2 ? urlParts[urlParts.length - 2] : null;
          const oldStoragePath = maybeFolder === user.id ? `${user.id}/${lastPart}` : lastPart;
          if (lastPart && (lastPart.includes(user.id) || maybeFolder === user.id)) {
            const { error: deleteError } = await supabase.storage
              .from('avatars')
              .remove([oldStoragePath]);
            if (deleteError) {
              console.warn('Failed to delete old avatar:', deleteError);
            } else {
              console.log('✅ Old avatar deleted:', oldStoragePath);
            }
          }
        } catch (deleteErr) {
          console.warn('Error deleting old avatar:', deleteErr);
          // Continue anyway
        }
      }

      // Upload avatar (no upsert to avoid RLS SELECT/UPDATE issues on storage.objects in some setups)
      console.log('📤 Uploading file to path:', filePath);
      let uploadResult = await supabase.storage
        .from('avatars')
        .upload(filePath, file, { cacheControl: '3600', upsert: false });
      if (uploadResult.error && uploadResult.error.message?.includes('already exists')) {
        uploadResult = await supabase.storage
          .from('avatars')
          .upload(filePath, file, { cacheControl: '3600', upsert: true });
      }
      const { data: uploadData, error: uploadError } = uploadResult;

      if (uploadError) {
        console.error('❌ Upload error details:', {
          message: uploadError.message,
          statusCode: uploadError.statusCode,
          error: uploadError.error,
          fullError: uploadError
        });
        const msg = (uploadError.message || uploadError.error || '').toString();
        if (msg.includes('Bucket not found') || msg.includes('does not exist') || uploadError.statusCode === 404) {
          throw new Error('مجلد الصور الشخصية غير موجود. يرجى التواصل مع الإدارة لإنشاء المجلد (avatars).');
        }
        if (msg.includes('row-level security') || msg.includes('policy') || msg.includes('RLS')) {
          throw new Error('صلاحية الرفع مرفوضة. تأكد من وجود سياسة التخزين للمجلد avatars.');
        }
        throw uploadError;
      }

      console.log('✅ Avatar uploaded:', uploadData);

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      console.log('📸 Public URL:', publicUrl);
      console.log('👤 User ID:', user.id);

      // Update user_profiles.avatar_url (avoid .select() after update — RLS may block it)
      console.log('🔄 Updating user_profiles with avatar_url...');
      const { error: updateError } = await supabase
        .from('user_profiles')
        .update({ avatar_url: publicUrl, updated_at: new Date().toISOString() })
        .eq('id', user.id);

      if (updateError) {
        console.error('❌ Update error:', updateError);
        const updateMsg = (updateError.message || updateError.details || '').toString();
        if (updateMsg.includes('row-level security') || updateMsg.includes('policy') || updateMsg.includes('RLS')) {
          throw new Error('صلاحية تحديث الملف الشخصي مرفوضة. تم رفع الصورة لكن لم يُحفظ الرابط في الملف الشخصي.');
        }
        throw updateError;
      }
      console.log('✅ user_profiles updated');

      // Update photographer_profiles.avatar_url so profile image is stored for the photographer row too
      const { error: ppUpdateError } = await supabase
        .from('photographer_profiles')
        .update({ avatar_url: publicUrl, updated_at: new Date().toISOString() })
        .eq('user_id', user.id);
      if (ppUpdateError) {
        console.warn('⚠️ photographer_profiles avatar_url update failed (non-fatal):', ppUpdateError);
      } else {
        console.log('✅ photographer_profiles.avatar_url updated');
        setPhotographerData((prev) => (prev ? { ...prev, avatar_url: publicUrl, avatarUrl: publicUrl } : null));
      }

      // Update local state immediately to show the new avatar
      setCurrentAvatarUrl(publicUrl);
      console.log('✅ currentAvatarUrl state updated to:', publicUrl);
      
      // Verify the update was successful by checking the database
      const { data: verifyData, error: verifyError } = await supabase
        .from('user_profiles')
        .select('avatar_url')
        .eq('id', user.id)
        .single();
      
      if (verifyError) {
        console.warn('⚠️ Could not verify avatar update:', verifyError);
      } else {
        console.log('✅ Verified avatar_url in database:', verifyData?.avatar_url);
        if (verifyData?.avatar_url !== publicUrl && verifyData?.avatar_url) {
          console.warn('⚠️ Avatar URL mismatch! Expected:', publicUrl, 'Got:', verifyData?.avatar_url);
        }
      }
      
      // Show success message
      setToast({ type: 'success', message: 'تم تحديث الصورة الشخصية بنجاح' });

      // تحديث الحالة مباشرةً بدون إعادة تحميل الصفحة — window.location.reload() كان يسبب
      // race condition على قفل Supabase Auth ويُظهر خطأ "فشل تحميل الملف الشخصي"
      await loadProfile();
    } catch (error) {
      console.error('❌ Failed to upload avatar:', error);
      let errorMessage = 'فشل رفع الصورة';
      const errStr = error?.message || error?.error || (typeof error === 'string' ? error : '');
      if (errStr) {
        errorMessage = errStr.length > 80 ? `فشل رفع الصورة: ${errStr.slice(0, 80)}...` : `فشل رفع الصورة: ${errStr}`;
      }
      setToast({ type: 'error', message: errorMessage });
    } finally {
      setSaving(false);
    }
  };

  // SAFE MODE: Certificates feature - نفس مكان الحفظ عند تسجيل مصور جديد
  // التخزين: bucket photographer-certificates، قاعدة البيانات: photographer_profiles.certificates
  const handleCertificateUpload = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) {
      setToast({ type: 'error', message: 'لم يتم اختيار ملف. اختر ملفاً (PDF أو JPG أو PNG).' });
      return;
    }
    if (!CERTIFICATE_ALLOWED_TYPES.includes(file.type)) {
      setToast({ type: 'error', message: 'نوع الملف غير مدعوم. يُقبل PDF، JPG، PNG فقط.' });
      return;
    }
    if (file.size > CERTIFICATE_MAX_SIZE) {
      setToast({ type: 'error', message: 'حجم الملف كبير جداً (الحد الأقصى 5MB)' });
      return;
    }
    setUploadingCertificates(true);
    setToast(null);
    try {
      const user = { id: userProfile?.id };
      if (!user.id) {
        setToast({ type: 'error', message: 'المستخدم غير مسجل دخول. سجّل الدخول ثم جرّب مرة أخرى.' });
        setUploadingCertificates(false);
        return;
      }
      const bucket = 'photographer-certificates';
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 100);
      const filePath = `${user.id}/${Date.now()}-${safeName}`;
      const { error: uploadErr } = await supabase.storage.from(bucket).upload(filePath, file, { cacheControl: '3600', upsert: false });
      if (uploadErr) {
        if (uploadErr.message?.includes('Bucket') || uploadErr.message?.includes('bucket') || uploadErr.message?.includes('not found')) {
          throw new Error('مجلد الشهادات غير موجود. يرجى تشغيل هجرة الشهادات على Supabase.');
        }
        throw uploadErr;
      }
      const { data: urlData } = supabase.storage.from(bucket).getPublicUrl(filePath);
      const newEntry = { url: urlData?.publicUrl || '', name: file.name };
      const nextList = [...(Array.isArray(certificates) ? certificates : []), newEntry];
      const { data: updatedRows, error: updateErr } = await supabase
        .from('photographer_profiles')
        .update({ certificates: nextList, updated_at: new Date().toISOString() })
        .eq('user_id', user.id)
        .select('certificates');
      if (updateErr) {
        if (updateErr.message?.includes('certificates') && (updateErr.message?.includes('does not exist') || updateErr.code === '42703')) {
          throw new Error('عمود الشهادات غير موجود. يرجى تشغيل هجرة 20260224000000_photographer_certificates.sql على Supabase.');
        }
        throw updateErr;
      }
      if (!updatedRows || updatedRows.length === 0) {
        throw new Error('لم يتم حفظ الشهادات. تأكد من صلاحيات الحساب.');
      }
      const savedCerts = updatedRows[0]?.certificates;
      setCertificates(Array.isArray(savedCerts) ? savedCerts : nextList);
      setToast({ type: 'success', message: 'تم رفع الشهادة وحفظها بنجاح' });
      setTimeout(() => loadProfile(), 800);
    } catch (err) {
      console.error('Certificate upload error:', err);
      setToast({ type: 'error', message: err?.message || 'فشل رفع الشهادة' });
    } finally {
      setUploadingCertificates(false);
      if (certificateInputRef.current) certificateInputRef.current.value = '';
    }
  };

  // SAFE MODE: Certificates feature - delete one certificate (storage + DB)
  const handleDeleteCertificate = async (index) => {
    const list = certificates || [];
    const item = list[index];
    if (!item?.url) return;
    const url = typeof item === 'string' ? item : item.url;
    const path = url.includes('photographer-certificates/') ? url.split('photographer-certificates/')[1] : null;
    if (!path) {
      setToast({ type: 'error', message: 'تعذر تحديد مسار الملف' });
      return;
    }
    try {
      setDeletingCertIndex(index);
      const user = { id: userProfile?.id };
      if (!user.id) throw new Error('Not authenticated');
      const { error: delErr } = await supabase.storage.from('photographer-certificates').remove([path]);
      if (delErr) console.warn('Storage delete warning:', delErr);
      const nextList = list.filter((_, i) => i !== index);
      setCertificates(nextList);
      const { error: updateErr } = await supabase.from('photographer_profiles').update({ certificates: nextList.length ? nextList : null, updated_at: new Date().toISOString() }).eq('user_id', user.id);
      if (updateErr) throw updateErr;
      setToast({ type: 'success', message: 'تم حذف الشهادة' });
    } catch (err) {
      console.error('Certificate delete error:', err);
      setToast({ type: 'error', message: err?.message || 'فشل حذف الشهادة' });
    } finally {
      setDeletingCertIndex(null);
    }
  };

  const calculateCompletion = () => {
    let completed = 0;
    const total = 8;

    if (photographerData?.avatar_url || photographerData?.avatarUrl || userProfile?.avatar_url) completed++;
    if (formData.city) completed++;
    if (formData.specializations.length > 0) completed++;
    if (formData.serviceCenterLat != null && formData.serviceCenterLng != null) completed++;
    if (formData.languages.length > 0) completed++;
    if (formData.equipment.length > 0) completed++;
    if (formData.shootingMethods?.length > 0) completed++;
    if (activePortfolioCount >= 3) completed++; // معتمدة + قيد المراجعة

    return Math.round((completed / total) * 100);
  };

  // هل وافق المشرف على الهوية؟
  const isIdentityApproved = !!(photographerData?.identityVerified || photographerData?.identity_verified);
  // هل رفع المصور وثيقة الهوية (بغض النظر عن الموافقة)؟
  const hasUploadedIdDocument = !!(photographerData?.idDocumentUrl || photographerData?.id_document_url);

  // دالة مساعدة تُعيد قائمة البنود الناقصة للعرض في الـ UI (بدون الهوية — شرط إداري مستقل)
  const getMissingItems = () => {
    const missing = [];
    if (!(photographerData?.avatar_url || photographerData?.avatarUrl || userProfile?.avatar_url))
      missing.push({ key: 'avatar', label: 'الصورة الشخصية', icon: 'User', link: '/photographer-dashboard/profile/form' });
    if (!formData.city) missing.push({ key: 'city', label: 'المدينة', icon: 'MapPin', link: '/photographer-dashboard/profile/form' });
    if (!formData.specializations.length) missing.push({ key: 'spec', label: 'التخصصات', icon: 'Camera', link: '/photographer-dashboard/profile/form' });
    if (!(formData.serviceCenterLat != null && formData.serviceCenterLng != null))
      missing.push({ key: 'serviceArea', label: 'نطاق الخدمة (حدد منطقة عملك)', icon: 'Map', link: '/photographer-dashboard/profile/service-area' });
    if (!formData.languages.length) missing.push({ key: 'lang', label: 'المعدات المتاحة', icon: 'Wrench', link: '/photographer-dashboard/profile/form' });
    if (!formData.equipment.length) missing.push({ key: 'editing', label: 'مستوى التعديل الذي تقدمه', icon: 'Sliders', link: '/photographer-dashboard/profile/form' });
    if (!formData.shootingMethods?.length)
      missing.push({ key: 'shooting', label: 'وسائل التصوير', icon: 'Camera', link: '/photographer-dashboard/profile/form' });
    if (activePortfolioCount < 3)
      missing.push({ key: 'portfolio', label: `معرض الأعمال (${activePortfolioCount}/3 صور)`, icon: 'Image', link: '/photographer-dashboard/portfolio-management' });
    return missing;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-slate-600">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  const completionPercentage = calculateCompletion();

  const listItemClass = 'w-full flex items-center gap-4 p-4 rounded-xl border border-slate-200 hover:bg-slate-50 hover:border-slate-300 transition-colors text-right';
  const listIconClass = 'w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0';
  const listLabelClass = 'font-medium text-slate-800';

  return (
    <div className="space-y-6">
      {/* حسابي - قائمة موحدة (تظهر فقط في الصفحة الرئيسية لحسابي) */}
      {isHub && (
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
            حسابي
          </h2>
          {(photographerData?.display_id != null || photographerData?.displayId != null) && (
            <span className="text-sm text-primary font-mono">
              R_{String(photographerData?.display_id ?? photographerData?.displayId).padStart(6, '0')}
            </span>
          )}
        </div>
        <div className="grid gap-3">
          <button type="button" onClick={() => navigate('/photographer-dashboard/profile/form')} className={listItemClass}>
            <div className={listIconClass}><AppIcon name="User" size={20} className="text-primary" /></div>
            <span className={listLabelClass}>الملف الشخصي</span>
            <span className="text-sm text-slate-500 mr-auto">اكتمال الملف: {completionPercentage}%</span>
            <AppIcon name="ChevronLeft" size={18} className="text-slate-400" />
          </button>
          <button type="button" onClick={() => navigate('/photographer-dashboard/profile/service-area')} className={listItemClass}>
            <div className={listIconClass}><AppIcon name="Map" size={20} className="text-primary" /></div>
            <span className={listLabelClass}>نطاق الخدمة</span>
            <AppIcon name="ChevronLeft" size={18} className="text-slate-400 mr-auto" />
          </button>
          <button type="button" onClick={() => navigate('/photographer-dashboard/profile/verification')} className={listItemClass}>
            <div className={listIconClass}><AppIcon name="ShieldCheck" size={20} className="text-primary" /></div>
            <span className={listLabelClass}>التحقق</span>
            <AppIcon name="ChevronLeft" size={18} className="text-slate-400 mr-auto" />
          </button>
          <button type="button" onClick={() => navigate('/photographer-dashboard/availability')} className={listItemClass}>
            <div className={listIconClass}><AppIcon name="Clock" size={20} className="text-primary" /></div>
            <span className={listLabelClass}>التوفر</span>
            <AppIcon name="ChevronLeft" size={18} className="text-slate-400 mr-auto" />
          </button>
          <button type="button" onClick={() => navigate('/photographer-dashboard/deliveries')} className={listItemClass}>
            <div className={listIconClass}><AppIcon name="Upload" size={20} className="text-primary" /></div>
            <span className={listLabelClass}>تسليم الصور والفيديوهات</span>
            <AppIcon name="ChevronLeft" size={18} className="text-slate-400 mr-auto" />
          </button>
          <button type="button" onClick={() => navigate('/photographer-dashboard/reviews')} className={listItemClass}>
            <div className={listIconClass}><AppIcon name="Star" size={20} className="text-primary" /></div>
            <span className={listLabelClass}>التقييمات</span>
            <AppIcon name="ChevronLeft" size={18} className="text-slate-400 mr-auto" />
          </button>
          <button type="button" onClick={() => navigate('/photographer-dashboard/notifications')} className={listItemClass}>
            <div className={listIconClass}><AppIcon name="Bell" size={20} className="text-primary" /></div>
            <span className={listLabelClass}>الإشعارات</span>
            <AppIcon name="ChevronLeft" size={18} className="text-slate-400 mr-auto" />
          </button>
          <button type="button" onClick={() => navigate('/photographer-dashboard/support')} className={listItemClass}>
            <div className={listIconClass}><AppIcon name="Headphones" size={20} className="text-primary" /></div>
            <span className={listLabelClass}>الدعم والشكاوى</span>
            <AppIcon name="ChevronLeft" size={18} className="text-slate-400 mr-auto" />
          </button>
          <button type="button" onClick={() => navigate('/photographer-dashboard/profile/settings')} className={listItemClass}>
            <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
              <AppIcon name="Settings" size={20} className="text-slate-600" />
            </div>
            <span className={listLabelClass}>الإعدادات</span>
            <AppIcon name="ChevronLeft" size={18} className="text-slate-400 mr-auto" />
          </button>
        </div>

        {/* معلومات قانونية */}
        <div className="border-t border-slate-200 pt-4 mt-2">
          <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">معلومات قانونية</p>
          <div className="grid gap-3">
            <button type="button" onClick={() => navigate('/about')} className={listItemClass}>
              <div className={listIconClass}><AppIcon name="Info" size={20} className="text-primary" /></div>
              <span className={listLabelClass}>من نحن</span>
              <AppIcon name="ChevronLeft" size={18} className="text-slate-400 mr-auto" />
            </button>
            <button type="button" onClick={() => navigate('/terms')} className={listItemClass}>
              <div className={listIconClass}><AppIcon name="ScrollText" size={20} className="text-primary" /></div>
              <span className={listLabelClass}>الشروط والأحكام</span>
              <AppIcon name="ChevronLeft" size={18} className="text-slate-400 mr-auto" />
            </button>
            <button type="button" onClick={() => navigate('/privacy')} className={listItemClass}>
              <div className={listIconClass}><AppIcon name="ShieldCheck" size={20} className="text-primary" /></div>
              <span className={listLabelClass}>سياسة الخصوصية</span>
              <AppIcon name="ChevronLeft" size={18} className="text-slate-400 mr-auto" />
            </button>
          </div>
        </div>

        <div className="border-t border-slate-200 pt-4 mt-4 space-y-3">
          <button
            type="button"
            onClick={async () => { try { await signOut(); navigate('/photographer-login'); } catch (e) { console.error('Logout error:', e); } }}
            className="w-full flex items-center gap-4 p-4 rounded-xl border border-slate-200 hover:bg-slate-50 text-slate-700 transition-colors text-right"
          >
            <AppIcon name="LogOut" size={20} className="text-slate-500" />
            <span className={listLabelClass}>تسجيل الخروج</span>
          </button>
          <button
            type="button"
            className="w-full flex items-center gap-4 p-4 rounded-xl border border-red-200 bg-red-50/50 hover:bg-red-50 text-red-700 transition-colors text-right"
            onClick={async () => {
              if (deletingAccount) return;
              const confirmed = window.confirm(
                'هل أنت متأكد من رغبتك في حذف حسابك نهائياً؟ لا يمكن التراجع عن هذا الإجراء.'
              );
              if (!confirmed) return;
              try {
                setDeletingAccount(true);
                await deleteOwnAccountViaEdgeFunction();
                try {
                  await signOut();
                } catch (e) {
                  console.warn('Sign out after delete failed (photographer):', e);
                }
                window.location.href = '/';
              } catch (e) {
                console.error('Delete account error:', e);
                alert(e?.message || 'فشل حذف الحساب، يرجى المحاولة لاحقاً.');
                setDeletingAccount(false);
              }
            }}
          >
            <AppIcon name="Trash2" size={20} className="text-red-500" />
            <span className={listLabelClass}>
              {deletingAccount ? 'جاري حذف الحساب...' : 'حذف الحساب'}
            </span>
          </button>
        </div>
      </div>
      )}

      {/* محتوى الملف الشخصي - يظهر فقط في صفحة الملف الشخصي */}
      {isForm && (
      <div id="profile-section" className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-6">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>الملف الشخصي</h2>
          <span className="text-sm font-semibold" style={{ color: completionPercentage === 100 ? '#16a34a' : '#6366f1', fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
            {completionPercentage}% مكتمل
          </span>
        </div>
        <div className="w-full bg-slate-200 rounded-full h-2 mb-1">
          <div
            className="h-2 rounded-full transition-all"
            style={{ width: `${completionPercentage}%`, backgroundColor: completionPercentage === 100 ? '#16a34a' : '#6366f1' }}
          />
        </div>
        {completionPercentage < 100 && (
          <div className="mb-4 p-3 rounded-xl bg-amber-50 border border-amber-200">
            <p className="text-xs font-semibold text-amber-700 mb-2" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
              ⚠️ يجب إكمال الملف الشخصي (100%) حتى تظهر في نتائج البحث. البنود الناقصة:
            </p>
            <ul className="space-y-1">
              {getMissingItems().map((item) => (
                <li key={item.key} style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
                  {item.link ? (
                    <button
                      type="button"
                      onClick={() => navigate(item.link)}
                      className="flex items-center gap-2 text-xs text-amber-800 hover:text-amber-600 underline underline-offset-2 transition-colors w-full text-right"
                    >
                      <AppIcon name={item.icon} size={12} className="shrink-0" />
                      <span>{item.label}</span>
                      <AppIcon name="ChevronLeft" size={10} className="mr-auto shrink-0 opacity-60" />
                    </button>
                  ) : (
                    <span className="flex items-center gap-2 text-xs text-amber-800">
                      <AppIcon name={item.icon} size={12} />
                      <span>{item.label}</span>
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* تنبيه الهوية — يظهر دائماً ما لم تُوافَق الهوية، بغض النظر عن نسبة الاكتمال */}
        {!isIdentityApproved && (
          <div className={`mb-4 p-3 rounded-xl border ${hasUploadedIdDocument ? 'bg-blue-50 border-blue-200' : 'bg-amber-50 border-amber-200'}`}>
            <div className="flex items-start gap-2">
              <AppIcon
                name={hasUploadedIdDocument ? 'Clock' : 'ShieldAlert'}
                size={14}
                className={`shrink-0 mt-0.5 ${hasUploadedIdDocument ? 'text-blue-600' : 'text-amber-600'}`}
              />
              <div className="flex-1">
                <p className={`text-xs font-semibold ${hasUploadedIdDocument ? 'text-blue-700' : 'text-amber-700'}`} style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
                  {hasUploadedIdDocument
                    ? 'وثيقة الهوية قيد المراجعة — ستظهر في البحث بعد موافقة المشرف'
                    : 'يجب رفع وثيقة الهوية للظهور في نتائج البحث'}
                </p>
                <button
                  type="button"
                  onClick={() => navigate('/photographer-dashboard/profile/verification')}
                  className={`mt-1 text-xs underline underline-offset-2 ${hasUploadedIdDocument ? 'text-blue-600 hover:text-blue-800' : 'text-amber-700 hover:text-amber-900'}`}
                  style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}
                >
                  {hasUploadedIdDocument ? 'عرض حالة التحقق' : 'رفع وثيقة الهوية'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* تنبيه الصور المرفوضة */}
        {rejectedPortfolioCount > 0 && (
          <div className="mb-4 p-3 rounded-xl border border-red-200 bg-red-50">
            <div className="flex items-start gap-2">
              <AppIcon name="XCircle" size={14} className="shrink-0 mt-0.5 text-red-600" />
              <div className="flex-1">
                <p className="text-xs font-semibold text-red-700" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
                  {rejectedPortfolioCount === 1
                    ? 'تم رفض صورة من معرض أعمالك — يرجى استبدالها'
                    : `تم رفض ${rejectedPortfolioCount} صور من معرض أعمالك — يرجى استبدالها`}
                </p>
                <button
                  type="button"
                  onClick={() => navigate('/photographer-dashboard/portfolio-management')}
                  className="mt-1 text-xs text-red-600 hover:text-red-800 underline underline-offset-2"
                  style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}
                >
                  عرض المعرض واستبدال الصور
                </button>
              </div>
            </div>
          </div>
        )}

        {completionPercentage === 100 && isIdentityApproved && (
          <p className="text-xs text-green-600 mb-4" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
            ✅ ملفك الشخصي مكتمل وأنت مرئي في نتائج البحث
          </p>
        )}
      {/* Profile Photo Section */}
      <div className="rounded-xl bg-slate-50/50 border border-slate-200 p-6">
        <h2 className="text-xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
          الصورة الشخصية
        </h2>
        <div className="flex items-center gap-6">
          <div className="relative">
            <div className="w-24 h-24 rounded-full bg-primary flex items-center justify-center overflow-hidden border-2 border-slate-200">
              {(currentAvatarUrl || photographerData?.avatar_url || photographerData?.avatarUrl || userProfile?.avatar_url) ? (
                <img
                  src={currentAvatarUrl || photographerData?.avatar_url || photographerData?.avatarUrl || userProfile?.avatar_url}
                  alt={userProfile?.full_name || 'مصور'}
                  className="w-full h-full object-cover"
                  onLoad={() => {
                    console.log('✅ Avatar image loaded successfully:', currentAvatarUrl || photographerData?.avatar_url || photographerData?.avatarUrl || userProfile?.avatar_url);
                  }}
                  onError={(e) => {
                    console.error('❌ Failed to load avatar image:', currentAvatarUrl || photographerData?.avatar_url || photographerData?.avatarUrl || userProfile?.avatar_url);
                    // Fallback to icon if image fails to load
                    e.target.style.display = 'none';
                  }}
                />
              ) : (
                <AppIcon name="User" size={48} className="text-white" />
              )}
            </div>
            {/* Add Photo Icon - Always Visible */}
            <label className="absolute bottom-0 right-0 w-10 h-10 bg-primary rounded-full flex items-center justify-center cursor-pointer shadow-lg hover:shadow-xl transition-all transform hover:scale-110 border-2 border-white">
              <input
                type="file"
                accept="image/*"
                onChange={handleAvatarUpload}
                className="hidden"
                disabled={saving}
              />
              <AppIcon name="Camera" size={20} className="text-white" />
            </label>
          </div>
          <div className="flex-1">
            <p className="text-sm text-slate-600 mb-2" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
              اضغط على أيقونة الكاميرا لإضافة أو تغيير صورتك الشخصية
            </p>
            <p className="text-xs text-slate-500">
              JPG, PNG أو GIF (حد أقصى 5MB)
            </p>
            {saving && (
              <div className="mt-2 flex items-center gap-2 text-sm text-primary">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                <span>جاري رفع الصورة...</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Profile Form */}
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="الاسم الاول والاخير "
            value={formData.fullName}
            onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
            required
          />

          <Input
            label="رقم الهاتف / الجوال"
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            placeholder="مثال: +966501234567"
            dir="ltr"
            readOnly
            disabled
            className="bg-slate-50 cursor-not-allowed"
          />

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              البريد الإلكتروني <span className="text-destructive">*</span>
            </label>
            <Input
              type="email"
              value={formData.contactEmail}
              onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
              placeholder="example@email.com"
              dir="ltr"
              className="text-left"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              المدينة <span className="text-destructive">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground" aria-hidden>
                <AppIcon name="ChevronDown" size={18} />
              </span>
              <select
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                className="select-no-native-arrow flex h-10 w-full rounded-md border border-input bg-background pl-10 pr-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                dir="rtl"
              >
                <option value="">اختر المدينة</option>
                {cities.map((city) => (
                  <option key={city} value={city}>
                    {city}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>



        {/* Photography Categories - Multi-Select */}
        <div>
          <Select
            label="فئات التصوير"
            options={photographyCategories}
            value={formData.specializations}
            onChange={(value) => setFormData({ ...formData, specializations: value })}
            multiple={true}
            placeholder="اختر فئات التصوير"
            clearable={true}
            searchable={true}
            description="يمكنك اختيار أكثر من فئة"
          />
          {formData.specializations.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {formData.specializations.map((val) => {
                const category = photographyCategories.find((cat) => cat.value === val);
                return category ? (
                  <span
                    key={val}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded-md text-xs"
                  >
                    {category.label}
                    <button
                      type="button"
                      onClick={() => {
                        setFormData({
                          ...formData,
                          specializations: formData.specializations.filter((v) => v !== val),
                        });
                      }}
                      className="hover:text-primary/80"
                    >
                      <AppIcon name="X" size={12} />
                    </button>
                  </span>
                ) : null;
              })}
            </div>
          )}
        </div>

        {/* Shooting Methods - Required */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            وسائل التصوير <span className="text-destructive">*</span>
          </label>
          <p className="text-xs text-muted-foreground mb-3">
            اختر وسيلة أو أكثر من وسائل التصوير التي تقدمها (يمكن اختيار أكثر من واحدة)
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {shootingMethodOptions.map((method) => {
              const isSelected = formData.shootingMethods.includes(method.value);
              return (
                <div key={method.value} className="flex flex-col gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      if (isSelected) {
                        const { [method.value]: _, ...rest } = formData.shootingMethodModels || {};
                        setFormData({
                          ...formData,
                          shootingMethods: formData.shootingMethods.filter((v) => v !== method.value),
                          shootingMethodModels: rest,
                        });
                      } else {
                        setFormData({
                          ...formData,
                          shootingMethods: [...formData.shootingMethods, method.value],
                        });
                      }
                    }}
                    className={`p-4 rounded-xl border-2 text-right transition-all hover:border-primary hover:bg-primary/5 ${
                      isSelected ? 'border-primary bg-primary/10 shadow-sm' : 'border-border bg-background'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-600/10 to-blue-500/10 flex items-center justify-center flex-shrink-0 border border-blue-200/50">
                            {method.value === 'drone' ? (
                              <DroneIcon size={20} className="text-blue-600" />
                            ) : (
                              <AppIcon name={method.icon} size={20} className="text-blue-600" />
                            )}
                          </div>
                          <div className="text-base font-semibold text-foreground">{method.label}</div>
                        </div>
                      </div>
                      {isSelected && <AppIcon name="CheckCircle2" size={20} color="var(--color-primary)" className="flex-shrink-0" />}
                    </div>
                  </button>

                  {/* حقل نوع الجهاز — إلزامي عند التحديد */}
                  {isSelected && (
                    <div className="flex flex-col gap-1">
                      <label className="text-xs font-semibold text-foreground/70">
                        النوع <span className="text-destructive">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.shootingMethodModels?.[method.value] || ''}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            shootingMethodModels: {
                              ...prev.shootingMethodModels,
                              [method.value]: e.target.value,
                            },
                          }))
                        }
                        placeholder={method.placeholder}
                        className={`w-full px-3 py-2 text-sm border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/30 text-right placeholder:text-muted-foreground ${
                          !(formData.shootingMethodModels?.[method.value] || '').trim()
                            ? 'border-destructive/50'
                            : 'border-primary/30'
                        }`}
                      />
                      {!(formData.shootingMethodModels?.[method.value] || '').trim() && (
                        <p className="text-xs text-destructive">هذا الحقل إلزامي</p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {formData.shootingMethods.length === 0 && (
            <p className="text-xs text-destructive mt-2">يجب اختيار وسيلة تصوير واحدة على الأقل</p>
          )}
        </div>

        {/* Editing Level - Radio Group */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            هل تقدم تعديلاً احترافياً لعملائك؟
          </label>
          <p className="text-xs text-slate-500 mb-3">
            إذا اخترت الاحترافي، ستظهر للعملاء الذين يطلبونه ويُضاف مبلغ إضافي تلقائياً على سعرك
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {equipmentOptions.map((option) => {
              const isSelected = formData.equipment[0] === option.value;
              const isPro = option.value === 'professional-editing';

              const secondaryLine = isPro
                ? 'تنقية كاملة ومعالجة خلفية وتفاصيل دقيقة — يُضاف مبلغ إضافي تلقائياً'
                : 'تصحيح الإضاءة والألوان والوضوح ';
              const toolsLine = isPro
                ? 'Lr · Ps · Pr · DaVinci وما شابهها'
                : 'Lr · Snapseed · CapCut وما شابهها';

              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setFormData({ ...formData, equipment: [option.value] })}
                  className={`p-4 rounded-xl border-2 text-right transition-all ${
                    isSelected && isPro
                      ? 'border-amber-500 bg-amber-50 shadow-sm'
                      : isSelected
                      ? 'border-primary bg-primary/10 shadow-sm'
                      : isPro
                      ? 'border-amber-300 bg-amber-50/40 hover:border-amber-400'
                      : 'border-slate-200 bg-white hover:border-primary/40'
                  }`}
                >
                  <div className={`font-semibold text-sm mb-1 ${isPro ? 'text-amber-800' : 'text-slate-800'}`}>
                    {isPro ? 'نعم، أقدم تعديل احترافي' : 'لا، اقدم تعديل اساسي فقط'}
                    {isPro && (
                      <span className="mr-2 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-amber-200 text-amber-800">
                        مميز
                      </span>
                    )}
                  </div>
                  <div className={`text-xs ${isPro ? 'text-amber-700 font-medium' : 'text-slate-500'}`}>
                    {secondaryLine}
                  </div>
                  <div className="text-[10px] text-slate-400 mt-1 font-mono">
                    {toolsLine}
                  </div>
                  {isSelected && (
                    <div className={`text-xs mt-1.5 font-medium ${isPro ? 'text-amber-600' : 'text-primary'}`}>
                      ✓ محدد
                    </div>
                  )}
                </button>
              );
            })}
          </div>
          {formData.equipment.length === 0 && (
            <p className="text-xs text-amber-600 mt-2">
              ⚠️ يجب اختيار مستوى التعديل لإكمال ملفك الشخصي والظهور في نتائج البحث
            </p>
          )}
        </div>

        {/* Languages */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            المعدات المتاحة
          </label>
          <div className="flex flex-wrap gap-3">
            {[
              { value: 'عدسة احترافية', label: 'عدسة احترافية' },
              { value: 'مانع اهتزاز', label: 'مانع اهتزاز' },
              { value: 'إضاءة استديو', label: 'إضاءة استديو' },
              { value: 'خلفيات تصوير', label: 'خلفيات تصوير' },
              { value: 'مايكروفون', label: 'مايكروفون' }
            ].map((lang) => (
              <label
                key={lang.value}
                className="flex items-center gap-2 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={formData.languages.includes(lang.value)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setFormData({
                        ...formData,
                        languages: [...formData.languages, lang.value]
                      });
                    } else {
                      setFormData({
                        ...formData,
                        languages: formData.languages.filter(l => l !== lang.value)
                      });
                    }
                  }}
                  className="w-4 h-4 text-primary border-slate-300 rounded focus:ring-primary"
                />
                <span className="text-sm text-foreground">{lang.label}</span>
              </label>
            ))}
          </div>
          {formData.languages.length > 0 && (
            <p className="text-xs text-slate-600 mt-2">
              المعدات المختارة: {formData.languages.join('، ')}
            </p>
          )}
        </div>

        {/* Studio Section */}
        <div className="border-t border-slate-200 pt-6 mt-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">معلومات الاستديو</h3>
          
          <div className="mb-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.hasStudio}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    hasStudio: e.target.checked,
                    studioLocation: e.target.checked ? (formData.studioLocation || {
                      address_text: '',
                      city: formData.city || '',
                      district: '',
                      street: '',
                      postal_code: '',
                      lat: null,
                      lng: null
                    }) : null
                  });
                }}
                className="w-4 h-4 text-primary border-slate-300 rounded focus:ring-primary"
              />
              <span className="text-sm font-medium text-foreground">هل لديك استديو تصوير؟</span>
            </label>
          </div>

          {formData.hasStudio && (
            <div className="space-y-4 bg-slate-50 rounded-lg p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    المدينة
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground" aria-hidden>
                      <AppIcon name="ChevronDown" size={18} />
                    </span>
                    <select
                      value={formData.studioLocation?.city || formData.city}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          studioLocation: {
                            ...formData.studioLocation,
                            city: e.target.value
                          }
                        });
                      }}
                      className="select-no-native-arrow flex h-10 w-full rounded-md border border-input bg-background pl-10 pr-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      dir="rtl"
                    >
                      <option value="">اختر المدينة</option>
                      {cities.map((city) => (
                        <option key={city} value={city}>
                          {city}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <Input
                  label="الحي / المنطقة"
                  value={formData.studioLocation?.district || ''}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      studioLocation: {
                        ...formData.studioLocation,
                        district: e.target.value
                      }
                    });
                  }}
                  placeholder="مثال: حي العليا"
                />
              </div>

              <Input
                label="الشارع"
                value={formData.studioLocation?.street || ''}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    studioLocation: {
                      ...formData.studioLocation,
                      street: e.target.value
                    }
                  });
                }}
                placeholder="اسم الشارع"
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="الرمز البريدي"
                  value={formData.studioLocation?.postal_code || ''}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      studioLocation: {
                        ...formData.studioLocation,
                        postal_code: e.target.value
                      }
                    });
                  }}
                  placeholder="12345"
                />

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    العنوان الكامل
                  </label>
                  <textarea
                    value={formData.studioLocation?.address_text || ''}
                    onChange={(e) => {
                      const addressText = e.target.value;
                      setFormData({
                        ...formData,
                        studioLocation: {
                          ...formData.studioLocation,
                          address_text: addressText
                        }
                      });
                    }}
                    rows={2}
                    className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    placeholder="العنوان الكامل للاستديو"
                  />
                </div>
              </div>

              <p className="text-xs text-slate-600">
                💡 سيتم استخدام عنوان الاستديو تلقائياً عندما يختار العميل "التصوير في استديو المصور"
              </p>
            </div>
          )}
        </div>

        <div className="flex justify-start pt-4 border-t border-slate-200">
          <Button onClick={handleSave} loading={saving} iconName="Save" size="lg">
            حفظ التغييرات
          </Button>
        </div>
      </div>
      </div>
      )}

      {/* نطاق الخدمة - يظهر فقط في صفحة نطاق الخدمة */}
      {isServiceArea && (
      <div id="service-area-section" className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
        <h2 className="text-xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>نطاق الخدمة <span className="text-destructive">*</span></h2>
        <p className="text-sm text-slate-600 mb-4">
          حدد مركز منطقة الخدمة على الخريطة. هذا الحقل إلزامي لتتمكن من تفعيل حالة "متاح للحجز".
        </p>

        {/* Service Center Location */}
        <div className="space-y-4 mb-4">
          {formData.serviceCenterLat && formData.serviceCenterLng ? (
            <div className="bg-success/10 border border-success/20 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AppIcon name="CheckCircle2" size={24} className="text-success flex-shrink-0 mt-0.5" />
                <div className="flex-1 text-right">
                  <p className="font-semibold text-success mb-1">تم تحديد مركز منطقة الخدمة</p>
                  {formData.serviceAreaLabel && (
                    <p className="text-sm text-slate-700">{formData.serviceAreaLabel}</p>
                  )}
                  <p className="text-xs text-slate-500 mt-1">
                    {formData.serviceCenterLat.toFixed(6)}, {formData.serviceCenterLng.toFixed(6)}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AppIcon name="AlertCircle" size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800">
                  حدد مركز منطقة الخدمة على الخريطة لإكمال ملفك
                </p>
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setServiceMapVisible(!serviceMapVisible)}
              iconName="Map"
              iconPosition="right"
              className="flex-1"
            >
              {serviceMapVisible ? 'إخفاء الخريطة' : 'تحديد على الخريطة'}
            </Button>
            <Button
              variant="outline"
              onClick={handleUseCurrentLocationForService}
              iconName="Navigation"
              iconPosition="right"
              className="flex-1"
            >
              استخدم موقعي الحالي
            </Button>
          </div>

          {serviceMapVisible && (
            <LocationPickerMap
              key={`service-map-${formData.serviceCenterLat || 'default'}-${formData.serviceCenterLng || 'default'}`}
              initialCenter={
                formData.serviceCenterLat && formData.serviceCenterLng
                  ? { lat: formData.serviceCenterLat, lng: formData.serviceCenterLng }
                  : formData.city
                    ? getCityCenter(formData.city)
                    : { lat: 24.7136, lng: 46.6753 }
              }
              onLocationSelect={handleServiceLocationSelect}
              onCancel={() => setServiceMapVisible(false)}
            />
          )}
        </div>

        {/* Service Radius — يُحفَظ تلقائياً عند التغيير */}
        <div className="mb-2">
          <label className="block text-sm font-medium text-foreground mb-2">
            نطاق الخدمة (كم)
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground" aria-hidden>
              <AppIcon name="ChevronDown" size={18} />
            </span>
            <select
              value={formData.serviceRadiusKm}
              onChange={async (e) => {
                const newRadius = parseInt(e.target.value);
                setFormData((prev) => ({ ...prev, serviceRadiusKm: newRadius }));
                // حفظ تلقائي عند تغيير النطاق (إن كان الموقع محدداً)
                const lat = serviceCenterRef.current?.lat ?? formData.serviceCenterLat;
                const lng = serviceCenterRef.current?.lng ?? formData.serviceCenterLng;
                if (lat != null && lng != null) {
                  try {
                    await _persistServiceArea(lat, lng, newRadius, formData.serviceAreaLabel || null);
                    setToast({ type: 'success', message: '✅ تم حفظ نطاق الخدمة تلقائياً' });
                  } catch {
                    setToast({ type: 'error', message: 'فشل حفظ النطاق. يرجى المحاولة مجدداً.' });
                  }
                }
              }}
              dir="rtl"
              className="select-no-native-arrow flex h-10 w-full rounded-md border border-input bg-background pl-10 pr-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value={5}>5 كم</option>
              <option value={10}>10 كم</option>
              <option value={20}>20 كم</option>
              <option value={30}>30 كم</option>
              <option value={50}>50 كم</option>
            </select>
          </div>
          <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
            <AppIcon name="Info" size={12} />
            يُحفَظ تلقائياً عند الاختيار — لا حاجة لضغط أي زر
          </p>
        </div>
      </div>
      )}

      {/* التحقق - يظهر فقط في صفحة التحقق */}
      {isVerification && (
        <div id="verification-section">
          <IdentityVerificationSection userId={userProfile?.id} />
        </div>
      )}

      {isSettings && (
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>الإعدادات</h2>
        </div>

        {/* تفعيل/إيقاف الإشعارات */}
        <div className="flex items-center justify-between gap-3 p-4 border border-slate-200 rounded-xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <AppIcon name="Bell" size={20} className="text-primary" />
            </div>
            <div>
              <p className="font-semibold text-slate-800">الإشعارات</p>
              <p className="text-sm text-slate-500">تلقي إشعارات الحجوزات والتحديثات</p>
            </div>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={notificationsEnabled}
            onClick={() => handleToggleNotifications(!notificationsEnabled)}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none ${
              notificationsEnabled ? 'bg-primary' : 'bg-slate-300'
            }`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
              notificationsEnabled ? 'translate-x-6' : 'translate-x-1'
            }`} />
          </button>
        </div>
      </div>
      )}

      {toast && (
        <Toast
          notification={{
            title: toast.type === 'success' ? 'تم بنجاح' : toast.type === 'error' ? 'خطأ' : 'تنبيه',
            message: toast.message,
            icon: toast.type === 'success' ? 'CheckCircle' : toast.type === 'error' ? 'AlertCircle' : 'Info',
            iconColor: toast.type === 'success' ? '#22c55e' : toast.type === 'error' ? '#ef4444' : '#f59e0b'
          }}
          onClose={() => setToast(null)}
          duration={5000}
        />
      )}
    </div>
  );
};

export default Profile;
